import { ROLE_LEVELS, useRoleLevel } from '@app/routes/RequireLevel';
import { useAppSelector } from '@app/store/hooks';
import {
	Assessment,
	Business,
	Category,
	Checklist,
	Dashboard,
	Inventory,
	LocalShipping,
	LocationOn,
	People,
	Security,
	ShoppingCart,
	Store,
	SwapHoriz,
	Warehouse,
	Warning,
} from '@mui/icons-material';
import {
	Box,
	Divider,
	Drawer,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Tooltip,
	Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

const DRAWER_WIDTH = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

interface MenuItem {
	label: string;
	path: string;
	icon: React.ReactNode;
	minLevel?: number; // Minimum required level to see this item
}

// Level 50+ (Operator) - Basic menu items
const menuItems: MenuItem[] = [
	{ label: 'Табло', path: '/dashboard', icon: <Dashboard /> },
	{ label: 'Продукти', path: '/products', icon: <Inventory /> },
	{ label: 'Категории', path: '/categories', icon: <Category /> },
	{ label: 'Палети', path: '/pallets', icon: <LocalShipping /> },
	{ label: 'Локации', path: '/locations', icon: <LocationOn /> },
	{ label: 'Поръчки', path: '/orders', icon: <ShoppingCart /> },
	{ label: 'Движения', path: '/movements', icon: <SwapHoriz /> },
	{
		label: 'Инвентаризации',
		path: '/stock-counts',
		icon: <Checklist />,
		minLevel: ROLE_LEVELS.MANAGER,
	},
	{ label: 'Изтичащи срокове', path: '/expiry-alerts', icon: <Warning /> },
];

// Level 75+ (Manager) for view, Level 100 for management
const managementItems: MenuItem[] = [
	{ label: 'Потребители', path: '/users', icon: <People />, minLevel: ROLE_LEVELS.MANAGER },
	{ label: 'Роли', path: '/roles', icon: <Security />, minLevel: ROLE_LEVELS.SUPER_ADMIN },
	{ label: 'Доставчици', path: '/suppliers', icon: <Business />, minLevel: ROLE_LEVELS.MANAGER },
	{ label: 'Клиенти', path: '/customers', icon: <Store />, minLevel: ROLE_LEVELS.MANAGER },
	{ label: 'Складове', path: '/warehouses', icon: <Warehouse />, minLevel: ROLE_LEVELS.MANAGER },
];

// Level 75+ (Manager) for reports
const systemItems: MenuItem[] = [
	{ label: 'Справки', path: '/reports', icon: <Assessment />, minLevel: ROLE_LEVELS.MANAGER },
];

export function Sidebar() {
	const navigate = useNavigate();
	const location = useLocation();
	const { sidebarCollapsed } = useAppSelector((state) => state.ui);
	const userLevel = useRoleLevel();
	const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

	// Filter menu items based on user's level
	const filterByLevel = (items: MenuItem[]) =>
		items.filter((item) => !item.minLevel || userLevel >= item.minLevel);

	const filteredMenuItems = filterByLevel(menuItems);
	const filteredManagementItems = filterByLevel(managementItems);
	const filteredSystemItems = filterByLevel(systemItems);

	const renderMenuItem = (item: MenuItem) => {
		const isActive = location.pathname === item.path;

		const button = (
			<ListItemButton
				onClick={() => navigate(item.path)}
				sx={{
					borderRadius: 1,
					mx: 1,
					mb: 0.5,
					bgcolor: isActive ? 'primary.main' : 'transparent',
					color: isActive ? 'white' : 'text.primary',
					'&:hover': {
						bgcolor: isActive ? 'primary.dark' : 'action.hover',
					},
					justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
					px: sidebarCollapsed ? 1 : 2,
				}}>
				<ListItemIcon
					sx={{
						color: isActive ? 'white' : 'text.secondary',
						minWidth: sidebarCollapsed ? 0 : 40,
					}}>
					{item.icon}
				</ListItemIcon>
				{!sidebarCollapsed && <ListItemText primary={item.label} />}
			</ListItemButton>
		);

		return (
			<ListItem key={item.path} disablePadding>
				{sidebarCollapsed ? (
					<Tooltip title={item.label} placement='right'>
						{button}
					</Tooltip>
				) : (
					button
				)}
			</ListItem>
		);
	};

	const renderSection = (title: string, items: MenuItem[]) => (
		<>
			{!sidebarCollapsed && (
				<Typography
					variant='caption'
					sx={{
						px: 3,
						py: 1,
						display: 'block',
						color: 'text.secondary',
						fontWeight: 600,
						textTransform: 'uppercase',
						letterSpacing: 0.5,
					}}>
					{title}
				</Typography>
			)}
			<List>{items.map(renderMenuItem)}</List>
		</>
	);

	return (
		<Drawer
			variant='permanent'
			sx={{
				width: drawerWidth,
				flexShrink: 0,
				'& .MuiDrawer-paper': {
					width: drawerWidth,
					boxSizing: 'border-box',
					borderRight: '1px solid',
					borderColor: 'divider',
					transition: 'width 0.2s ease-in-out',
				},
			}}>
			{/* Logo */}
			<Box
				sx={{
					p: 2,
					display: 'flex',
					alignItems: 'center',
					justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
					gap: 1,
				}}>
				<Warehouse sx={{ fontSize: 32, color: 'primary.main' }} />
				{!sidebarCollapsed && (
					<Typography variant='h6' fontWeight='bold'>
						WMS Admin
					</Typography>
				)}
			</Box>

			<Divider />

			{/* Main Menu - Level 50+ */}
			{renderSection('Основно', filteredMenuItems)}

			{/* Management - Level 75+ for some items */}
			{filteredManagementItems.length > 0 && (
				<>
					<Divider sx={{ my: 1 }} />
					{renderSection('Управление', filteredManagementItems)}
				</>
			)}

			{/* System - Level 75+ */}
			{filteredSystemItems.length > 0 && (
				<>
					<Divider sx={{ my: 1 }} />
					{renderSection('Система', filteredSystemItems)}
				</>
			)}
		</Drawer>
	);
}
